// src/components/arrowquest/ArrowQuestLanding.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Page = styled.div`
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #1a2940;
    background: linear-gradient(180deg, #E8F1FC 0%, #FFFFFF 100%);
    min-height: 100vh;
`;

const Container = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: 4rem 1.5rem;
`;

const Hero = styled.section`
    text-align: center;
    padding: 5rem 1rem 4rem;
`;

const Logo = styled.div`
    display: inline-block;
    background: #2E86EE;
    color: white;
    font-weight: 900;
    font-size: 1rem;
    letter-spacing: 1px;
    padding: 0.4rem 1rem;
    border-radius: 999px;
    margin-bottom: 1.5rem;
`;

const Title = styled.h1`
    font-size: clamp(2.5rem, 6vw, 4.5rem);
    font-weight: 900;
    margin: 0 0 1rem;
    color: #2E86EE;
    line-height: 1.1;
`;

const Subtitle = styled.p`
    font-size: clamp(1rem, 2.5vw, 1.25rem);
    color: #5a6a82;
    max-width: 600px;
    margin: 0 auto 2.5rem;
    line-height: 1.6;
`;

const PlayBtn = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    background: #2E86EE;
    color: white;
    padding: 0.9rem 2rem;
    border-radius: 999px;
    font-weight: 700;
    text-decoration: none;
    box-shadow: 0 8px 24px rgba(46, 134, 238, 0.3);
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 32px rgba(46, 134, 238, 0.4);
    }
`;

const Features = styled.section`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1.5rem;
    margin: 4rem 0;
`;

const FeatureCard = styled.div`
    background: white;
    padding: 2rem 1.5rem;
    border-radius: 16px;
    text-align: center;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    transition: transform 0.2s;

    &:hover {
        transform: translateY(-4px);
    }

    .icon {
        font-size: 2.5rem;
        margin-bottom: 0.8rem;
    }

    h3 {
        font-size: 1.15rem;
        color: #2E86EE;
        margin-bottom: 0.5rem;
    }

    p {
        font-size: 0.95rem;
        color: #5a6a82;
        line-height: 1.5;
    }
`;

const InfoSection = styled.section`
    background: white;
    padding: 3rem 2rem;
    border-radius: 20px;
    margin: 3rem 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);

    h2 {
        color: #2E86EE;
        font-size: 1.75rem;
        margin-bottom: 1rem;
    }

    p {
        color: #5a6a82;
        line-height: 1.7;
        margin-bottom: 0.8rem;
    }
`;

const LegalLinks = styled.section`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1.5rem;
    margin: 3rem 0 1rem;
    padding-top: 2rem;
    border-top: 1px solid #d8e3f0;

    a {
        color: #2E86EE;
        text-decoration: none;
        font-weight: 600;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const Footer = styled.footer`
    text-align: center;
    padding: 2rem 1rem 1rem;
    color: #8593a8;
    font-size: 0.9rem;

    a {
        color: #2E86EE;
    }
`;

const ArrowQuestLanding = () => {
    const features = [
        { icon: '🏹', title: 'Arrow Puzzles', desc: 'Solve hundreds of arrow-flipping puzzles across increasing difficulty.' },
        { icon: '🧠', title: 'Brain Training', desc: 'Sharpen your logic and pattern recognition in short sessions.' },
        { icon: '📅', title: 'Daily Challenge', desc: 'A new puzzle every day. Build a streak, climb the ranks.' },
        { icon: '🎁', title: 'Free to Play', desc: 'Free with optional rewards and ad removal as in-app purchases.' },
    ];

    return (
        <Page>
            <Container>
                <Hero>
                    <Logo>MINDWORLD GAMES</Logo>
                    <Title>Arrow Quest</Title>
                    <Subtitle>
                        A casual logic puzzle game. Flip the arrows, clear the grid, and grow your puzzle-solving skills one level at a time.
                    </Subtitle>
                    <PlayBtn href="https://play.google.com/store" target="_blank" rel="noopener noreferrer">
                        ▶ Get it on Google Play (Coming Soon)
                    </PlayBtn>
                </Hero>

                <Features>
                    {features.map((f) => (
                        <FeatureCard key={f.title}>
                            <div className="icon">{f.icon}</div>
                            <h3>{f.title}</h3>
                            <p>{f.desc}</p>
                        </FeatureCard>
                    ))}
                </Features>

                <InfoSection>
                    <h2>About Arrow Quest</h2>
                    <p>
                        Arrow Quest is a free-to-play casual puzzle game for Android. Tap arrows to change their direction, then chain them to clear the level. Simple to learn, hard to master.
                    </p>
                    <p>
                        Built by MindWorld, an indie studio focused on calming, brain-friendly mobile games.
                    </p>
                </InfoSection>

                <InfoSection>
                    <h2>Privacy & Ads</h2>
                    <p>
                        Arrow Quest is supported by ads served through Google AdMob. We respect your privacy and comply with GDPR, CCPA, and Korean PIPA. You can manage advertising consent any time in the game settings.
                    </p>
                    <p>
                        No account registration required. Game progress is stored locally on your device.
                    </p>
                </InfoSection>

                <LegalLinks>
                    <Link to="/arrowquest/privacy">Privacy Policy</Link>
                    <Link to="/arrowquest/terms">Terms of Service</Link>
                    <Link to="/arrowquest/account-deletion">Data Deletion</Link>
                    <a href="mailto:mind-wellness@naver.com">Contact Support</a>
                </LegalLinks>

                <Footer>
                    © {new Date().getFullYear()} MindWorld · Arrow Quest · <a href="mailto:mind-wellness@naver.com">mind-wellness@naver.com</a>
                </Footer>
            </Container>
        </Page>
    );
};

export default ArrowQuestLanding;
